import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, ShieldAlert, Sparkles } from "lucide-react";
import Navbar from "../components/site/Navbar";
import ContactRail from "../components/site/ContactRail";
import { Footer } from "../components/site/Sections5";
import PageHero from "../components/site/PageHero";
import DedicatedCTA from "../components/site/DedicatedCTA";
import SEO from "../components/site/SEO";
import { getServiceBySlug, SERVICES } from "../constants/servicesData";
import { getIndustryBySlug } from "../constants/industriesData";
import { trackViewContent } from "../lib/metaPixel";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  useEffect(() => {
    if (service) {
      trackViewContent({
        title: service.title,
        category: "Services",
        slug: service.slug,
      });
    }
  }, [service]);

  if (!service) {
    return (
      <div className="min-h-screen bg-navy flex flex-col justify-center items-center text-white px-5 text-center">
        <SEO title="Service Not Found" />
        <h1 className="font-display text-4xl font-bold uppercase sm:text-6xl">Service Not Found</h1>
        <p className="mt-4 font-body text-base text-white/70 max-w-md">
          The requested service page does not exist or may have been updated.
        </p>
        <Link
          to="/services"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#FF5722] px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white"
        >
          View All Services <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  const related = (service.relatedServices || [])
    .map((sSlug) => getServiceBySlug(sSlug))
    .filter(Boolean);

  const relatedInds = (service.relatedIndustries || [])
    .map((iSlug) => getIndustryBySlug(iSlug))
    .filter(Boolean);

  return (
    <div className="bg-white min-h-screen flex flex-col" data-testid={`service-detail-${service.slug}`}>
      <SEO
        title={`${service.title} | ADMALL INDIA`}
        description={service.summary}
        canonical={`https://admallindia.com/services/${service.slug}`}
      />
      <Navbar logo="/admall-logo.png" />
      <ContactRail />

      <main className="flex-grow">
        <PageHero
          category="Services"
          categoryLink="/services"
          title={service.title}
          tagline={service.tagline}
          bgImage={service.heroImage}
          ctaLabel={`Enquire About ${service.title}`}
          ctaHref="/#contact"
        />

        {/* Overview & Strategic Summary */}
        <section className="bg-white py-20 md:py-28 border-b border-navy/10">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-7">
                <div className="mb-4 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-widest text-[#FF5722]">
                  <span className="h-2 w-2 rounded-full bg-[#FF5722]" />
                  <span>Strategic Overview</span>
                </div>
                <h2 className="font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-navy sm:text-5xl">
                  {service.tagline}
                </h2>
                <p className="mt-6 font-body text-base leading-relaxed text-navy/70 sm:text-lg">
                  {service.summary}
                </p>
                <div className="mt-8 rounded-2xl bg-[#F8F9FA] p-6 border border-navy/10">
                  <div className="flex items-start gap-3">
                    <Sparkles size={20} className="text-[#FF5722] shrink-0 mt-1" />
                    <div>
                      <h4 className="font-display text-base font-bold uppercase tracking-wider text-navy">
                        The ADMALL Standard
                      </h4>
                      <p className="mt-1 font-body text-sm leading-relaxed text-navy/70">
                        {service.strategicInsight}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="relative overflow-hidden rounded-3xl shadow-xl border border-navy/10">
                  <img
                    src={service.heroImage}
                    alt={service.title}
                    className="aspect-[4/3] w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <span className="font-body text-xs font-semibold uppercase tracking-widest text-[#FF5722]">
                      ADMALL INDIA Studio
                    </span>
                    <p className="font-display text-xl font-bold uppercase mt-1">
                      Executed In-House • Senior Led
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* The Commercial Challenges Solved */}
        {service.painPoints && service.painPoints.length > 0 && (
          <section className="bg-[#F8F9FA] py-20 md:py-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
              <div className="max-w-3xl mb-14">
                <div className="mb-3 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-widest text-[#FF5722]">
                  <span className="h-2 w-2 rounded-full bg-[#FF5722]" />
                  <span>Market Reality</span>
                </div>
                <h3 className="font-display text-3xl font-extrabold uppercase tracking-tight text-navy sm:text-4xl">
                  Commercial Challenges We Eliminate
                </h3>
                <p className="mt-4 font-body text-base text-navy/60">
                  We look at marketing through a commercial lens. If your current efforts suffer from any of these friction points, {service.title} provides the remedy.
                </p>
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                {service.painPoints.map((point, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-4 rounded-2xl bg-white p-7 border border-navy/10 shadow-sm"
                  >
                    <ShieldAlert size={22} className="text-[#FF5722] shrink-0 mt-0.5" />
                    <p className="font-body text-base leading-relaxed text-navy/80">
                      {point}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Deliverables & Capabilities Grid */}
        <section className="bg-white py-20 md:py-28">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="max-w-3xl mb-14">
              <div className="mb-3 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-widest text-[#FF5722]">
                <span className="h-2 w-2 rounded-full bg-[#FF5722]" />
                <span>Scope & Execution</span>
              </div>
              <h3 className="font-display text-3xl font-extrabold uppercase tracking-tight text-navy sm:text-5xl">
                What We Deliver
              </h3>
              <p className="mt-4 font-body text-base text-navy/60">
                Transparent deliverables engineered for durability and business performance.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {service.deliverables.map((item, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl border border-navy/10 bg-white p-8 transition hover:border-[#FF5722] hover:shadow-lg flex flex-col justify-between"
                >
                  <div>
                    <span className="h-2 w-2 rounded-full bg-[#FF5722] block mb-4" />
                    <h4 className="font-display text-xl font-bold uppercase tracking-tight text-navy">
                      {item.title}
                    </h4>
                    <p className="mt-3 font-body text-sm leading-relaxed text-navy/60">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Industries Callout */}
        {relatedInds.length > 0 && (
          <section className="bg-[#0B132B] py-20 text-white">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                <div>
                  <div className="mb-3 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-widest text-[#FF5722]">
                    <span className="h-2 w-2 rounded-full bg-[#FF5722]" />
                    <span>Sector Relevance</span>
                  </div>
                  <h3 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
                    Industries Benefiting From {service.title}
                  </h3>
                </div>
                <Link
                  to="/industries"
                  className="inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-[#FF5722] hover:underline"
                >
                  View All Industries <ArrowRight size={16} />
                </Link>
              </div>

              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {relatedInds.map((ind) => (
                  <Link
                    key={ind.slug}
                    to={`/industries/${ind.slug}`}
                    className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-6 transition hover:border-[#FF5722] hover:bg-white/10"
                  >
                    <h4 className="font-display text-2xl font-bold uppercase tracking-tight text-white group-hover:text-[#FF5722] transition">
                      {ind.title}
                    </h4>
                    <p className="mt-2 font-body text-sm text-white/60 line-clamp-2">
                      {ind.tagline}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1 font-display text-xs font-bold uppercase tracking-wider text-[#FF5722]">
                      Explore Vertical <ArrowRight size={14} />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Related Services Navigation */}
        {related.length > 0 && (
          <section className="bg-[#F8F9FA] py-20">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
              <div className="mb-10">
                <span className="h-2 w-2 rounded-full bg-[#FF5722] inline-block mb-3 mr-2" />
                <span className="font-body text-xs font-bold uppercase tracking-widest text-[#FF5722]">
                  Connected System
                </span>
                <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-navy sm:text-3xl mt-1">
                  Complementary Services
                </h3>
              </div>

              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/services/${rel.slug}`}
                    className="group rounded-2xl border border-navy/10 bg-white p-7 transition hover:border-[#FF5722] hover:shadow-md flex flex-col justify-between"
                  >
                    <div>
                      <h4 className="font-display text-xl font-bold uppercase tracking-tight text-navy group-hover:text-[#FF5722] transition">
                        {rel.title}
                      </h4>
                      <p className="mt-2 font-body text-xs leading-relaxed text-navy/60 line-clamp-2">
                        {rel.tagline}
                      </p>
                    </div>
                    <span className="mt-5 inline-flex items-center gap-1 font-display text-xs font-bold uppercase tracking-wider text-[#FF5722]">
                      View Service <ArrowRight size={14} />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <DedicatedCTA
          title={`Ready to build your ${service.title} roadmap?`}
          subtitle={`Schedule a direct growth session with our team in Navi Mumbai. We will assess your market opportunity and provide clear strategic recommendations.`}
        />
      </main>

      <Footer
        content={{
          logo_url: "/admall-logo.png",
          footer: {
            tagline: "THINK. CREATE. REACH. PERFORM. GROW.",
            disciplines: "Brand Strategy | Creative | Multimedia | Digital Marketing | Advertising | Lead Generation | AI Marketing",
          },
          settings: {
            phone: "+91 93221 06632",
            phone_raw: "+919322106632",
            email: "admallindiagroup@gmail.com",
            location: "Navi Mumbai, Maharashtra, India",
            website: "admallindia.com",
          },
        }}
      />
    </div>
  );
}
